/**
 * Utility: Component Setup
 *
 * - Description: Funcs used by components during their setup
 *
 * @module internal/IComponentSetup
 */


// Dependancies
// ============================================================================

import { $ } from "../../common/utility/u-jquery-$";
import { IComponentUtils } from "./s-component-utils";


// Internal Variables
// ============================================================================

//@todo: Use this instead + add an accessor func, so other components can access
// the full data set with a single imported ƒ
const _STORE = {};

const _indexes = {
	// Auto incremented with each component init, so must start with -1
	// (which means, no components)
	all: -1,

	// Each component also has their own index, eg:
	// accordion: 2,  // = 3 accordions
	// dropdown:  1,  // = 2 dropdowns
}

// Same as index +1
const _counts = {
	all: 0,

	// Each component also has their own count, eg:
	// accordion: 3,  // = 3 accordions
	// dropdown:  2,  // = 2 dropdowns
}


// ƒ
// ============================================================================

/**
 * Set up the global `window.AppComponents`
 *
 * @return  {void}
 */
function setupGlobalComponentsObj()
{
	const { getComponentData, getComponentIndex, getComponentNumber, getComponentInstance, hasComponent } = IComponentUtils;

	// Stores component instances that are initialised on a jQuery element object
	window.AppComponents = {

		// Counts for all components, plus separate counts for each component instance
		// Updated in each component setup via updatePropsAndIndexes
		counts: {
			all: 0,

			// Example if there are 2 accordion instances:
			// accordion: 2,
		},

		// Indexes for all components, plus separate indexes for each component instance
		// Updated in each component setup via updatePropsAndIndexes
		indexes: {
			all: 0,

			// Example if there are 2 accordion instances:
			// accordion: 1,
		},

		// Utility funcs which are always available:
		utility: {
			getComponentData,
			getComponentInstance,
			getComponentIndex,
			getComponentNumber,
			hasComponent,
		},

		// Data store for all initialised components
		components: {
			/*
			// Each initialised component will be added here.
			// Each component property holds an array of objects, which have 2 props (instance & container)
			accordion: [
				{
					instance:  AccordionInstance,   // class instance (Class)
					container: $componentContainer, // jQuery element object
				},
			]
			*/
		}
	};
}


/**
 * Core component init
 *
 * @example setupComponent( 'example', 'data-c-example', {'option1':true}, setupItem, false )
 *
 * @param   {object}   setupOpts              Setup options object
 * @param   {string}   setupOpts.name         Component name (eg "example")
 * @param   {string}   setupOpts.dataAttr     Component's data attribute (eg "data-c-example")
 * @param   {object}   setupOpts.defaultOpts  Default options object. Can be an empty object if the component has no options
 * @param   {class}    setupOpts.Class        Class for a single item. Passes 3 args: $container, options, and instance index
 * @param   {boolean}  setupOpts.debugSetup   True to enable setup debug logging (logs $container, and parsed options). Defaults to false
 *
 * @return  {void}
 */
function setupComponent( setupOpts )
{
	jQuery( document ).ready( function( $ )
	{
		const { name, dataAttr, defaultOpts, Class, debugSetup } = setupOpts;
		const $containers = $( `[${dataAttr}]` );

		// Uses hardcoded option passed to this ƒ
		// Only ever true if a developer is working on this setup ƒ, and has set
		// debugSetup to true in a component's setupOpts object
		if ( debugSetup )
		{
			console.info( `[SETUP:${name}] 🔨 Component setup started`, { setupOpts } );
		}

		if ( !$containers.length )
		{
			if ( debugSetup )
			{
				console.info( `[SETUP:${name}] 🔨 Component setup cancelled: No containers found`, { setupOpts } );
			}

			return;
		}

		// Check that the default options has the `debugComponent` property
		// (and log an error if not)
		if ( !defaultOpts.hasOwnProperty( 'debugComponent' ) )
		{
			console.error( `[SETUP:${name}] 🔨 This component's defaults options should include the (bool) property "debugComponent". (This is an internal code issue, and can be fixed by updating the component.)`, { componentOptions: cOptions } );
			return;
		}

		// Loop through each container, and set up an invidual class instance for
		// each one
		$containers.each( ( instanceIndex, containerEl ) =>
		{
			const $container = $( containerEl );
			const cOptions    = setupComponentOptions( $container, setupOpts );

			if ( debugSetup )
			{
				console.info( `[SETUP:${name}] 🔨 Found 1x container`, { $container, cOptions } );
			}

			// Uses option per component instance
			// True if enabled in the data-c-{name} options
			if ( cOptions.debugComponent )
			{
				console.info( `[c-${name}] 💡 Component init started`, { data: { $container, instanceIndex } } );
			}

			// Init new instance of the component class
			const ComponentInstance = new Class( $container, cOptions, instanceIndex );

			ComponentInstance._classInit();

			updatePropsAndIndexes( name, $container, ComponentInstance, instanceIndex );

			if ( cOptions.debugComponent )
			{
				console.info( `[c-${name}] 💡 Component init done`, { data: { $container, ComponentInstance, instanceIndex } } );
			}
		});

		if ( debugSetup )
		{
			console.info( `[SETUP:${name}] 🔨 Component setup finished`, { setupOpts } );
		}
	});
}


/**
 * Parses a JSON options string, from a data attribute on the specified element
 *
 * Returns parsed options, merged with provided defaults
 *
 * @example setupComponentOptions( $container, setupOpts )
 *
 * @param   {jQuery}   $container             jQuery container element (which has the data attribute)
 * @param   {object}   setupOpts              Setup options object
 * @param   {string}   setupOpts.name         Component name
 * @param   {string}   setupOpts.dataAttr     Data attribute, which holds the options
 * @param   {object}   setupOpts.defaultOpts  Default options, these will be merged with any custom options passed via the data attr
 * @param   {class}    setupOpts.Class        Class for a single item. Passes 3 args: $container, options, and instance index
 * @param   {boolean}  setupOpts.debugSetup   True to enable setup debug logging. Defaults to false
 *
 * @return  {object}                          Modified options object, if user options were set. If no options were set, or a JSON parse error occured, returns the defaultOpts object
 */
function setupComponentOptions( $container, setupOpts )
{
	const { name, dataAttr, defaultOpts, debugSetup } = setupOpts;

	if ( !$container.attr( dataAttr ) )
	{
		return defaultOpts;
	}

	const customOptionsStr = $container.attr( dataAttr );

	// Clone defaultOpts to options
	// We're using an empty object here to preserve the original defaultOpts
	const options = Object.assign( {}, defaultOpts );

	if ( customOptionsStr )
	{
		if ( debugSetup )
		{
			console.info( `[c-${name}] Custom options were set`, $container );
		}

		// Attempt JSON parse
		try
		{
			const parsedData = JSON.parse( customOptionsStr );

			Object.assign( options, parsedData );

			if ( debugSetup )
			{
				console.info( `[c-${name}] Parsed options:`, parsedData );
			}
		}
		catch( err )
		{
			console.group( `[c-${name}] ERROR` );
			console.error( 'Failed to parse custom options. Please check that the options string is valid JSON. Defaults will be used.', { $container: $container, jsonString: customOptionsStr } );
			console.error( err );
			console.groupEnd();
		}
	}

	return options;
};



/**
 * Update props and indexes, for the local store vars and the global
 * AppComponents
 *
 * @todo: Split this up, it's far too long and hard to read
 *
 * @param   {string}  name               Component name
 * @param   {jQuery}  $container         Container
 * @param   {class}   ComponentInstance  Component class instance
 * @param   {int}     instanceIndex      Component instance index
 *
 * @return  {void}
 */
function updatePropsAndIndexes( name, $container, ComponentInstance, instanceIndex )
{
	// Update index/count - Internal
	// ------------------------------------------------------------------------

	// Ensure the local prop exists in components.indexes.{name}
	if ( !_indexes.hasOwnProperty( name ) )
	{
		_indexes[name] = 0; // starts the index at 0
	}

	// Same as above, but for _counts
	if ( !_counts.hasOwnProperty( name ) )
	{
		_counts[name] = 1; // starts the count at 1
	}

	_indexes.all++; // all components
	_indexes[name] = instanceIndex; // current component

	_counts.all    = _indexes.all  + 1; // all components
	_counts[name] = instanceIndex + 1; // current component


	// Update index/count - Global (AppComponents)
	// ------------------------------------------------------------------------

	// Update the global indexes object
	// The -1 accounts for the increment at the end of this func
	window.AppComponents.indexes = _indexes;
	window.AppComponents.counts  = _counts;

	// Ensure the AppComponents.components.{component} property exists
	if ( !window.AppComponents.components.hasOwnProperty( name ) )
	{
		window.AppComponents.components[name] = [];
	}

	// Add this instance's info to AppComponents.{name}[]
	//@todo: Store this data locally first instead, then copy the stored obj to the global
	window.AppComponents.components[name][_indexes[name]] = {
		container: $container,
		instance: ComponentInstance,
	};


	// Save indexes to the jQuery element object's data
	// ------------------------------------------------------------------------

	let elComponentsData = $container.data( '_components' );

	// Ensure the $container has data for "_components"
	if ( typeof ( elComponentsData ) === 'undefined' )
	{
		// Set the default data
		elComponentsData = {
			index: {
				all:     0,
				[name]: 0,
			},
			number: { // similar to count
				all:     1,
				[name]: 1,
			},
			components: {
				[name]: {},
			},
		};
	}

	elComponentsData.index.all    = _indexes.all;
	elComponentsData.index[name] = _indexes[name];

	elComponentsData.number.all     = _counts.all;
	elComponentsData.number[name]  = _counts[name];

	elComponentsData.components[name] = {
		instance:       ComponentInstance,
		instanceIndex:  instanceIndex,
		instanceNumber: instanceIndex + 1,
	}

	$container.data( '_components', elComponentsData );
}


// Exports
// ============================================================================

export const IComponentSetup = { setupGlobalComponentsObj, setupComponent, setupComponentOptions };
